import React, { useCallback, useEffect, useState } from 'react';
import { AppState, Modal, Platform, StyleSheet, View } from 'react-native';
import { Button, Typography } from '../ui';
import { ONBOARDING_TOUR_ENABLED } from '../../config/flags';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { surfaceFill } from '../../styles/platformBackgrounds';
import { isAuthRoute } from '../../navigation/authRoutes';
import { navigateTo } from '../../navigation/rootNavigation';
import { useOnboardingTour } from '../../onboarding/OnboardingTourProvider';
import { isTourPausedOn } from '../../onboarding/tourRoutes';
import { refreshAgapellaAccess, setAgapellaAccess, useAgapellaAccess } from '../../services/agapellaAccess';
import { useAgapellaPaywallRequest } from '../../services/agapellaGate';
import { analytics } from '../../services/analytics';
import { api } from '../../services/api';
import { useCanShowExternalPurchase } from '../../services/paymentSurface';
import {
  priceLine,
  trialOfferWaitsForTour,
  trialMoment,
  trialMomentCopy,
  trialStartErrorLine,
  trialStartedLine,
  type TrialMoment,
  type TrialSeen,
} from '../../screens/subscription/agapellaSubscription';
import type { AgapellaAccessState, AgapellaSubscriptionOffer } from '../../types/agapellaSubscription';
import { isAutoGeneratedDisplayName } from '../../utils/isAutoGeneratedDisplayName';
import { storage } from '../../utils/storage';
import { useCurrentRouteName } from '../../widget/useCurrentRouteName';

const IS_WEB = Platform.OS === 'web';
const TOUR_RECHECK_MS = 4000;

/**
 * CORE-1538 — окно про пробный доступ Agapēlla поверх приложения (веб и натив).
 *
 * Три момента, каждый один раз на аккаунт (`trialMoment`):
 * - оффер после знакомства с платформой — «Начать 7 дней» и обязательное
 *   «Продолжить без подписки» (v3.4 §6);
 * - напоминание в последние двое суток trial;
 * - «пробный доступ закончился» — дальше бесплатный слой.
 *
 * Кнопка оплаты в двух последних — по правилу страны (CORE-1535) и только
 * когда подписка продаётся: Android вне РФ и iOS не видят ни кнопки, ни цены,
 * ни слов о покупке.
 *
 * Пусто, пока сервер не включил подписку этому человеку (флаг
 * `agapella_subscription`).
 */
export const AgapellaTrialGate: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const route = useCurrentRouteName();
  const tour = useOnboardingTour();
  const state = useAgapellaAccess();
  // CORE-1776 — открыто окно «Входит в Agapēlla»: второе окно поверх не всплывает.
  const paywallOpen = useAgapellaPaywallRequest() !== null;
  const userId = isAuthenticated ? user?.id ?? null : null;

  const [seen, setSeen] = useState<{ userId: string; value: TrialSeen } | null>(null);
  // Ответ сервера о туре: `null` — ещё не знаем. Тур выключен — ждать нечего.
  const [tourServer, setTourServer] = useState<{ shouldAutoLaunch: boolean } | null>(
    ONBOARDING_TOUR_ENABLED ? null : { shouldAutoLaunch: false },
  );
  const [started, setStarted] = useState<AgapellaAccessState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStarted(null);
    setError(null);
    if (!userId) { setSeen(null); return; }
    let alive = true;
    void storage.getTrialMomentsSeen(userId).then((value) => { if (alive) setSeen({ userId, value }); });
    return () => { alive = false; };
  }, [userId]);

  // Trial кончается по времени на сервере — узнать об этом можно, только
  // спросив заново. Возврат в приложение — естественный повод.
  useEffect(() => {
    const sub = AppState.addEventListener('change', (s) => {
      if (s === 'active' && userId) void refreshAgapellaAccess();
    });
    return () => sub.remove();
  }, [userId]);

  const moment = seen && seen.userId === userId ? trialMoment(state, seen.value) : null;
  const tourBusy = !!tour?.isActive || !!tour?.farewell;

  // Серверное состояние тура перечитываем, когда он гаснет: идущий тур виден
  // провайдеру, а сервер нужен, чтобы знать, не стартует ли тур сам вот-вот.
  // CORE-1750 — тур идёт и в приложении, поэтому оффер и там ждёт его конца, а
  // не всплывает окном поверх шторки с туром. Правило — trialOfferWaitsForTour.
  useEffect(() => {
    if (!ONBOARDING_TOUR_ENABLED || moment !== 'offer' || tourBusy) return;
    let alive = true;
    let retry: ReturnType<typeof setTimeout> | null = null;
    const read = (again: boolean) => {
      void api.getOnboardingTour()
        .then((dto) => {
          if (!alive) return;
          setTourServer({ shouldAutoLaunch: dto.shouldAutoLaunch });
          // Сервер ещё предлагает тур: автозапуск вот-вот, или тур только что
          // погас, а его запись ещё едет на сервер, — переспросить один раз,
          // иначе оффер отложился бы до следующего захода.
          if (dto.shouldAutoLaunch && again) retry = setTimeout(() => read(false), TOUR_RECHECK_MS);
        })
        .catch(() => { /* состояния тура не знаем — оффер подождёт следующего захода */ });
    };
    read(true);
    return () => { alive = false; if (retry) clearTimeout(retry); };
  }, [moment, tourBusy]);

  const markSeen = useCallback((m: string) => {
    if (!userId) return;
    setSeen((prev) => (prev && prev.userId === userId ? { userId, value: { ...prev.value, [m]: true } } : prev));
    void storage.setTrialMomentSeen(userId, m);
  }, [userId]);

  const startTrial = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await api.startAgapellaTrial();
      analytics.track('agapella_trial_started', { platform: Platform.OS, surface: 'offer' });
      setStarted(next);
      setAgapellaAccess(next);
    } catch (e) {
      setError(trialStartErrorLine(e));
      // Отказ «уже был» значит, что наше состояние устарело — перечитать.
      void refreshAgapellaAccess();
    } finally {
      setBusy(false);
    }
  }, []);

  const dismiss = useCallback((m: string, action: string) => {
    analytics.track('agapella_trial_moment_closed', { moment: m, action, platform: Platform.OS });
    markSeen(m);
  }, [markSeen]);

  const toPayment = useCallback((m: string) => {
    dismiss(m, 'pay');
    // Веб — страница подписки; приложение — карточка в «Настройках», она же
    // открывает внешний браузер и встречает возврат с оплаты (CORE-1537).
    if (IS_WEB) navigateTo('AgapellaSubscription');
    else navigateTo('Settings', { checkout: true });
  }, [dismiss]);

  if (!userId || !user || paywallOpen) return null;
  if (isAuthRoute(route) || isTourPausedOn(route) || route === 'AgapellaSubscription') return null;
  if (isAutoGeneratedDisplayName(user.displayName)) return null;

  if (started) {
    const close = () => { setStarted(null); markSeen('offer'); };
    return (
      <TrialCard
        testID="agapella-trial-started"
        title="Пробный доступ открыт"
        lines={[trialStartedLine(started), 'Напомню за пару дней до конца']}
        onClose={close}
      >
        <Button variant="solar" size="lg" onPress={close} testID="agapella-trial-started-ok">Хорошо</Button>
      </TrialCard>
    );
  }

  if (!moment) return null;

  if (moment === 'offer') {
    if (trialOfferWaitsForTour({ onScreen: tourBusy, server: tourServer })) return null;
    const copy = trialMomentCopy('offer', state, false);
    const skip = () => dismiss('offer', 'skip');
    return (
      <TrialCard testID="agapella-trial-offer" title={copy.title} lines={copy.lines} error={error} onClose={skip}>
        <Button
          variant="solar"
          size="lg"
          isLoading={busy}
          disabled={busy}
          onPress={() => { void startTrial(); }}
          testID="agapella-trial-offer-start"
        >
          Начать 7 дней
        </Button>
        <Button variant="ghost" disabled={busy} onPress={skip} testID="agapella-trial-offer-skip">
          Продолжить без подписки
        </Button>
      </TrialCard>
    );
  }

  return <TrialEndNotice moment={moment} state={state} onClose={dismiss} onPay={toPayment} />;
};

/**
 * Напоминание и «закончился». Отдельным компонентом, чтобы страну (Play
 * Billing, событие `payment_surface_decided`) спрашивать только у тех, кому
 * это окно действительно показываем, а не у каждого на старте приложения.
 */
const TrialEndNotice: React.FC<{
  moment: Exclude<TrialMoment, 'offer'>;
  state: AgapellaAccessState | null;
  onClose: (moment: string, action: string) => void;
  onPay: (moment: string) => void;
}> = ({ moment, state, onClose, onPay }) => {
  const externalPurchase = useCanShowExternalPurchase();
  const [offer, setOffer] = useState<AgapellaSubscriptionOffer | null | undefined>(undefined);

  useEffect(() => {
    if (externalPurchase !== true) return;
    let alive = true;
    void api.getAgapellaSubscriptionOffer()
      .then((o) => { if (alive) setOffer(o); })
      .catch(() => { if (alive) setOffer(null); });
    return () => { alive = false; };
  }, [externalPurchase]);

  // Пока не решено, будет ли кнопка оплаты, окна нет: иначе она появилась бы
  // в уже открытом окне.
  if (externalPurchase === null || (externalPurchase && offer === undefined)) return null;
  const canPay = externalPurchase && !!offer?.forSale;
  const copy = trialMomentCopy(moment, state, canPay);
  const price = canPay && offer ? priceLine(offer) : null;
  const close = () => onClose(moment, 'close');
  return (
    <TrialCard
      testID={`agapella-trial-${moment}`}
      title={copy.title}
      lines={price ? [...copy.lines, price] : copy.lines}
      onClose={close}
    >
      {canPay && (
        <Button variant="solar" size="lg" onPress={() => onPay(moment)} testID={`agapella-trial-${moment}-pay`}>
          Перейти к оплате
        </Button>
      )}
      <Button variant={canPay ? 'ghost' : 'solar'} onPress={close} testID={`agapella-trial-${moment}-close`}>
        {canPay ? 'Не сейчас' : 'Понятно'}
      </Button>
    </TrialCard>
  );
};

export const TrialCard: React.FC<{
  testID: string;
  title: string;
  lines: string[];
  error?: string | null;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ testID, title, lines, error, onClose, children }) => {
  const { colors: C, isDark } = useTheme();
  // CORE-1733 (возврат QA 23.10) — окно на вебе — непрозрачная поверхность v4, а не canvas #FFFFFF.
  const cardFill = Platform.OS === 'web' ? surfaceFill(isDark ? 'dark' : 'light') : { backgroundColor: C.canvas, borderColor: C.border };
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={s.overlay}>
        <View style={[s.card, cardFill]} testID={testID}>
          <Typography variant="h3" style={{ color: C.textPrimary }}>{title}</Typography>
          {lines.map((line) => (
            <Typography key={line} variant="body" style={{ color: C.textSecondary }}>{line}</Typography>
          ))}
          {!!error && <Typography variant="bodySmall" style={{ color: C.error }}>{error}</Typography>}
          <View style={s.actions}>{children}</View>
        </View>
      </View>
    </Modal>
  );
};

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  card: {
    width: '100%',
    maxWidth: 460,
    borderRadius: 20,
    borderWidth: 1,
    padding: 24,
    gap: 12,
    ...(Platform.OS === 'web' ? { boxShadow: '0 12px 40px rgba(0,0,0,0.25)' as any } : {}),
  },
  actions: { gap: 8, marginTop: 8 },
});
